// ===== FORMAT TYPE CONSTANTS =====

// Mirrors `DietechShareUtilsVBCore.modReportUtils.ReportFormatType`.
// Drives Print / Combine & Export button enable/disable in the queue —
// only NativeFormat rows go through the DevExpress print+combine pipeline;
// PDF and Excel format rows export via their own apps and can't be combined.
export const REPORT_FORMAT_TYPE = {
  NATIVE_FORMAT: 1,
  PDF: 2,
  EXCEL: 3,
} as const;
export type ReportFormatType = (typeof REPORT_FORMAT_TYPE)[keyof typeof REPORT_FORMAT_TYPE];

// ===== STATUS CONSTANTS =====

export const REPORT_STATUS = {
  PENDING: 0,
  GENERATING: 1,
  COMPLETED: 2,
  ERROR: 3,
  NO_DATA: 4,
  REQUIRED_INFO: 5,
  INFORMATION: 6,
  INFO_SELECT: 7,
  PRINTED: 8,
  CANCELED: 9,
  // Display only, never stored: a report opened on screen is saved as Printed (8)
  // with no Last Printed date, so the desktop shows it Printed. See reportDisplayStatus.
  VIEWED: 10,
} as const;
export type ReportStatus = (typeof REPORT_STATUS)[keyof typeof REPORT_STATUS];

// Each status renders in a unique pastel palette so the print
// queue is scannable at a glance — no two statuses share a bg
// color anymore. Palette pulls from the shared BADGE_PALETTE slot
// list in `utils/badgePalette.ts` (kept in sync visually).
export const REPORT_STATUS_CONFIG: Record<
  ReportStatus,
  { label: string; color: string; bgColor: string }
> = {
  [REPORT_STATUS.PENDING]:       { label: 'Pending',     color: '#92400e', bgColor: '#fef3c7' }, // amber
  [REPORT_STATUS.GENERATING]:    { label: 'Generating',  color: '#ffffff', bgColor: '#2563eb' }, // the one solid-fill pill — an "in flight" state that reads as active work at a glance, distinct from every other pill (all others are pastel fills with dark text). Text is white, not the light orange it used to be: orange on this blue measures 3.0:1 and was genuinely hard to read at chip size. White clears AA at 5.1:1 and keeps the solid-pill distinction.
  [REPORT_STATUS.COMPLETED]:     { label: 'Completed',   color: '#166534', bgColor: '#dcfce7' }, // green
  [REPORT_STATUS.ERROR]:         { label: 'Error',       color: '#991b1b', bgColor: '#fee2e2' }, // red-800. Was red-600 (#dc2626) — the only pill not on the -800 shade of its hue, and at 3.95:1 the only other one under AA
  [REPORT_STATUS.NO_DATA]:       { label: 'No Data',     color: '#475569', bgColor: '#f1f5f9' }, // slate
  [REPORT_STATUS.REQUIRED_INFO]: { label: 'Info Needed', color: '#6b21a8', bgColor: '#f3e8ff' }, // purple
  [REPORT_STATUS.INFORMATION]:   { label: 'Info',        color: '#075985', bgColor: '#e0f2fe' }, // sky
  [REPORT_STATUS.INFO_SELECT]:   { label: 'Select',      color: '#115e59', bgColor: '#ccfbf1' }, // teal
  [REPORT_STATUS.PRINTED]:       { label: 'Printed',     color: '#3730a3', bgColor: '#e0e7ff' }, // indigo — terminal "done" state. Cool-toned so it's clearly distinct from the warm amber Pending pill (they used to be amber-vs-orange and read as near-identical to users)
  [REPORT_STATUS.CANCELED]:      { label: 'Canceled',    color: '#9f1239', bgColor: '#ffe4e6' }, // rose
  [REPORT_STATUS.VIEWED]:        { label: 'Viewed',      color: '#1e40af', bgColor: '#dbeafe' }, // pastel blue: the step between Completed (green) and Printed (indigo). Pastel, so it never reads as the solid-blue Generating pill
};

/**
 * The status to show for a document. Opening a report on screen saves it as
 * Printed (8) without a Last Printed date (the desktop has no Viewed status);
 * every real print sets that date. So Printed with no date shows as Viewed.
 */
export function reportDisplayStatus(doc: { status: number; lastPrintedDateTime?: string | null }): number {
  return doc.status === REPORT_STATUS.PRINTED && !doc.lastPrintedDateTime ? REPORT_STATUS.VIEWED : doc.status;
}

export const SCHEDULE_LOG_STATUS = {
  QUEUED: 'Queued',
  RUNNING: 'Running',
  SUCCESS: 'Success',
  FAILED: 'Failed',
  CANCELLED: 'Cancelled',
} as const;

// Category → section mapping for grouping/sorting reports.
// Every category MUST route to a real top-level section
// (`Patient` / `Facility` / `Financial` / `Production` / `Employees`).
// The legacy `Temp` catch-all section has been removed — reports
// whose `groupName` isn't a key in this map are hidden from the
// sidebar. Migrate new reports via `PKEY_GROUP_OVERRIDES` in
// AllReports.tsx to set their groupName to a category key here.
// `displayName` overrides the sidebar/pill label when the KEY has
// to stay unique but the human-readable name should be simpler
// than the qualified section-prefixed key.
export const CATEGORY_CONFIG: Record<string, { order: number; section: string; displayName?: string }> = {
  // ── Patient (facility-labeled) ─────────────────────────────────
  'Patient/Acceptance':      { order: 5, section: 'Patient', displayName: 'Acceptance' },
  'Patient/Beverage':        { order: 6, section: 'Patient', displayName: 'Beverage' },
  'Patient/Birthdays':       { order: 7, section: 'Patient', displayName: 'Birthdays' },
  'Patient/Diet Order':      { order: 8, section: 'Patient', displayName: 'Diet Order' },
  'Patient/Labels':          { order: 8.5, section: 'Patient', displayName: 'Labels' },
  'Patient/Management Team': { order: 9, section: 'Patient', displayName: 'Management Team' },
  'Patient/Multiples':       { order: 9.5, section: 'Patient', displayName: 'Multiples' },
  'Patient/Nourishment':     { order: 10, section: 'Patient', displayName: 'Nourishments' },
  'Patient/Plan of Care':    { order: 10.5, section: 'Patient', displayName: 'Plan of Care' },
  'Patient/Weight':          { order: 11, section: 'Patient', displayName: 'Weight' },

  // ── Production (new top-level section, sibling of Patient) ─────
  'Production/Dish Up Labels': { order: 5, section: 'Production', displayName: 'Dish Up Labels' },
  'Production/Posting Menus':  { order: 6, section: 'Production', displayName: 'Posting Menus' },
  'Production/Purchasing':     { order: 7, section: 'Production', displayName: 'Purchasing' },
  'Production/Tally':          { order: 8, section: 'Production', displayName: 'Tally' },
  'Production/Selective Menu': { order: 9, section: 'Production', displayName: 'Selective Menu' },
  'Production/Tray Tickets':   { order: 10, section: 'Production', displayName: 'Tray Tickets' },

  // ── Facility (new top-level section — sidebar renders the
  //    facility-configured `facility` label instead of "Facility") ─
  'Facility/Portion':            { order: 5, section: 'Facility', displayName: 'Portion' },
  'Facility/Feeding Status':     { order: 6, section: 'Facility', displayName: 'Feeding Status' },
  'Facility/Med Pass':           { order: 7, section: 'Facility', displayName: 'Med Pass' },
  // `{SpecialDevice}` resolves via the sidebar renderer to
  // `useGlobalStore().specialDeviceLabel` (`tblFacility.SpecialDevice`
  // column — e.g. "Adaptive Equipment", "Special Device", etc.).
  // Rendered verbatim — no pluralization.
  'Facility/Special Devices':    { order: 8, section: 'Facility', displayName: '{SpecialDevice}' },
  'Facility/Census':             { order: 9, section: 'Facility', displayName: 'Census' },
  // `{Prescriber}` resolves via the sidebar renderer to the
  // facility-configured `prescriberLabel` (e.g. "Doctor").
  'Facility/Prescriber':         { order: 10, section: 'Facility', displayName: '{Prescriber}' },
  'Facility/Drug Allergies':     { order: 11, section: 'Facility', displayName: 'Drug Allergies' },
  'Facility/Food Allergies':     { order: 12, section: 'Facility', displayName: 'Food Allergies' },
  'Facility/Menu Cycle':         { order: 13, section: 'Facility', displayName: 'Menu Cycle' },
  'Facility/Nutrition':          { order: 14, section: 'Facility', displayName: 'Nutrition' },
  'Facility/Skin':               { order: 15, section: 'Facility', displayName: 'Skin' },
  'Facility/Special Needs':      { order: 16, section: 'Facility', displayName: 'Special Needs' },
  // `{EHR_Title}` resolves via the sidebar renderer to
  // `useGlobalStore().ehrTitleLabel` (`tblFacility.EHR_Title`
  // column — e.g. "EMR", "EHR", "Meditech"). Facility section
  // sorts alphabetically so `order` here just fills the slot.
  'Facility/EMR Integration':    { order: 17, section: 'Facility', displayName: '{EHR_Title} Integration' },
  'Facility/Recipes':            { order: 18, section: 'Facility', displayName: 'Recipes' },
  'Facility/Supplement':         { order: 19, section: 'Facility', displayName: 'Supplement' },
  'Facility/Tray Line':          { order: 20, section: 'Facility', displayName: 'Tray Line' },
  'Facility/Tube Feed':          { order: 21, section: 'Facility', displayName: 'Tube Feed' },

  // ── Employees (new top-level section) ──────────────────────────
  'Employees/Charge Summary':    { order: 5, section: 'Employees', displayName: 'Charge Summary' },
  'Employees/Production Tally':  { order: 6, section: 'Employees', displayName: 'Production Tally' },
  'Employees/Tray Tickets':      { order: 7, section: 'Employees', displayName: 'Tray Tickets' },

  // ── Financial (new top-level section, sibling of Patient) ──────
  'Financial/Cost Analysis': { order: 5, section: 'Financial', displayName: 'Cost Analysis' },
  // `{Credits}` resolves via the sidebar renderer to
  // `useGlobalStore().creditsLabel` — facilities can rename this
  // to "Bucks" / "Meal Credits" / etc. via
  // `tblFacility.Credits_MenuTitle`.
  'Financial/Credits':      { order: 6, section: 'Financial', displayName: '{Credits}' },

  // Temp section removed — legacy "one-time migration bucket" that
  // held ~40 unclassified categories. Every report should now
  // route through one of the real top-level sections above via
  // `PKEY_GROUP_OVERRIDES` in AllReports.tsx. Reports whose
  // `groupName` no longer resolves to a category here are hidden
  // by the sidebar's classification filter (see AllReports.tsx —
  // reports with unresolvable sections are skipped rather than
  // rolling up under a hidden "Other" bucket).
};

// Top-level sections. Keys are the persisted identifier used by
// the dashboard layout; user-facing labels for facility-scoped
// sections like Patient come from the global store's
// `residentLabel` (see AllReports sidebar render).
export const SECTION_ORDER: Record<string, number> = {
  'Production': 0,
  'Patient':    1,
  'Facility':   2,
  'Financial':  3,
  'Employees':  4,
};

/** Visual config for each section - colors and icons for sidebar & card accents.
 *  Note: the sidebar label for the 'Facility' section renders the
 *  facility-configured `facility` label (tblFacility.Facility column)
 *  via `facilityLabel` from `useGlobalStore` — resolved in
 *  `AllReports.tsx` where the sidebar iterates sections. */
export const SECTION_CONFIG: Record<string, { icon: string; color: string; bgColor: string }> = {
  'Patient':    { icon: 'bi-person-vcard', color: '#7c3aed', bgColor: '#ede9fe' },
  'Facility':   { icon: 'bi-building',     color: '#0891b2', bgColor: '#cffafe' },
  'Financial':  { icon: 'bi-cash-stack',   color: '#059669', bgColor: '#d1fae5' },
  'Production': { icon: 'bi-gear',         color: '#ea580c', bgColor: '#ffedd5' },
  'Employees':  { icon: 'bi-people-fill',  color: '#0284c7', bgColor: '#e0f2fe' },
};

/** Per-category icons for card view */
export const CATEGORY_ICONS: Record<string, string> = {
  'Diet Order':       'bi-clipboard2-pulse',
  'Tray Line':        'bi-ticket-detailed',
  'Meal':             'bi-cup-hot',
  'Beverage':         'bi-cup-straw',
  'Nourishment':      'bi-egg-fried',
  'Acceptance':       'bi-check-circle',
  'Resident':         'bi-person-vcard',
  'Weight':           'bi-speedometer',
  'Allergy':          'bi-exclamation-triangle',
  'Skin':             'bi-bandaid',
  'Tube Feed':        'bi-droplet',
  'Supplement':       'bi-capsule',
  'Lab Data':         'bi-activity',
  'Special':          'bi-star',
  'Posting Menu':     'bi-megaphone',
  'Production':       'bi-boxes',
  'Selective Menu':   'bi-hand-index',
  'Recipe':           'bi-book',
  'Purchasing':       'bi-cart3',
  'Nutritional':      'bi-heart-pulse',
  'Cost Analysis':    'bi-currency-dollar',
  'Intake Study':     'bi-clipboard-data',
  'Notes':            'bi-journal-text',
  'Prescriber':       'bi-prescription2',
  'Med Pass':         'bi-capsule',
  'Meal Pattern':     'bi-calendar3',
  'Item':             'bi-box-seam',
  'Labels':           'bi-tag',
  'Employee':         'bi-person-badge',
  'Management Team':  'bi-bar-chart-line',
  'Chargeable':       'bi-currency-dollar',
  'Fiber':            'bi-bar-chart-line',
  'Protein':          'bi-bar-chart-line',
  'Thickener':        'bi-bar-chart-line',
  'Special Requests': 'bi-chat-square-text',
  'Administrative':   'bi-building',
  'Bulk':             'bi-collection',
  'EHR/EMR':          'bi-hospital',
  'Credits':          'bi-receipt',
  'Miscellaneous':    'bi-folder',
};

// ===== CUSTOMIZABLE DASHBOARD LAYOUT =====

export interface ReportDashboardLayoutSection {
  name: string;
  visible: boolean;
  order: number;
}

export interface ReportDashboardLayoutCategory {
  name: string;
  section: string;
  visible: boolean;
  order: number;
}

export interface ReportDashboardLayout {
  version: 1;
  sections: ReportDashboardLayoutSection[];
  categories: ReportDashboardLayoutCategory[];
  hiddenReportIds: number[];
}

/** Build the default layout from the hardcoded SECTION_ORDER and CATEGORY_CONFIG */
export function buildDefaultLayout(): ReportDashboardLayout {
  const sections: ReportDashboardLayoutSection[] = Object.entries(SECTION_ORDER)
    .sort(([, a], [, b]) => a - b)
    .map(([name, order]) => ({ name, visible: true, order }));

  const categories: ReportDashboardLayoutCategory[] = Object.entries(CATEGORY_CONFIG)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([name, cfg]) => ({ name, section: cfg.section, visible: true, order: cfg.order }));

  return { version: 1, sections, categories, hiddenReportIds: [] };
}

/**
 * Merge a stored layout with current defaults so new sections/categories
 * added to the codebase appear automatically, and removed ones are pruned.
 */
export function mergeLayoutWithDefaults(stored: ReportDashboardLayout): ReportDashboardLayout {
  const defaults = buildDefaultLayout();

  // Merge sections: keep stored order/visibility, append new sections at the end
  const storedSectionNames = new Set(stored.sections.map((s) => s.name));
  const defaultSectionNames = new Set(defaults.sections.map((s) => s.name));
  const mergedSections = stored.sections
    .filter((s) => defaultSectionNames.has(s.name)) // prune removed sections
    .concat(
      defaults.sections
        .filter((s) => !storedSectionNames.has(s.name))
        .map((s, i) => ({ ...s, order: 100 + i })), // new sections at end
    );

  // Merge categories: same strategy
  const storedCatNames = new Set(stored.categories.map((c) => c.name));
  const defaultCatNames = new Set(defaults.categories.map((c) => c.name));
  const mergedCategories = stored.categories
    .filter((c) => defaultCatNames.has(c.name))
    .concat(
      defaults.categories
        .filter((c) => !storedCatNames.has(c.name))
        .map((c, i) => ({ ...c, order: 100 + i })),
    );

  return {
    version: 1,
    sections: mergedSections,
    categories: mergedCategories,
    hiddenReportIds: stored.hiddenReportIds,
  };
}

// ===== FACILITY SETS / RECIPES (for report selectors) =====

export interface FacilitySet {
  pKey_Facility_Sets: number;
  fKey_Facility: number;
  mealtime: number;
  name: string | null;
  number: number;
  /** 0 = (None Specified), 1 = Eats In Room, 2 = a dining room. */
  type: number;
  /** Set only when `type` is 2 — which dining room the set eats in. */
  fKey_LayoutDiningRoom?: number | null;
  /**
   * Auto Tray Unit — the unit that takes this set automatically when a
   * resident is added or moved. The only column the set-up screen's
   * save-time audit note diffs.
   */
  fKey_Unit_Mapping?: number | null;
}

/** One row of the Auto Tray Unit picker, in Floor/Wing order. */
export interface FacilitySetUnitOption {
  pKey_Unit_Mapping: number;
  unit: string;
  floor: string;
  wing: string;
}

/** The highest set number any live resident holds, per mealtime. */
export interface FacilitySetMax {
  maxB: number;
  maxL: number;
  maxD: number;
}

export interface RecipeItem {
  pKey_RC_Recipe: number;
  name: string;
  menuName: string | null;
  recipeNumber: string | null;
}

// ===== CORE DATA MODELS =====

export interface Report {
  pKey_RS_Report: number;
  reportName: string;
  reportDescription: string;
  version: number;
  reportSpeed: number;
  programName: string;
  groupName: string;
  summary: string;
  details: string;
  slug: string;
  nameForList: string;
}

export interface ReportDocument {
  pKey_RS_ReportDocument: number;
  fKey_RS_Report: number;
  fKey_RS_ScheduledJob: number;
  fKey_User: number;
  fKey_Facility: number;
  dateTimeCreated: string;
  status: number;
  pageCount: number;
  progressPosition: number;
  reportInputDataJson: string;
  statusMessage: string;
  /** Generator detail (a stack trace on failed rows). The API sends it to PSI staff only; null for everyone else. */
  statusDescription: string | null;
  lastPrintedDateTime: string | null;
  guid: string;
  devXVersion: string;
  autoPrint: boolean;
  category: number;
  reportName: string;
  detailedReportName: string;
  dateFor: string;
  settings: string;
  createdBy: string;
  testIncrementValue: number;
  statusName: string;
  generatedOn: string | null;
  printedBy: string;
  deletedBy: string;
  dateTimeDeleted: string | null;
  dateTimeStarted: string | null;
  dateTimeCanceled: string | null;
  canceledBy: string;
  cacheDataUsed: number;
  remoteJobID: string;
  fKey_User_Delete: number | null;
  fKey_User_Canceled: number | null;
  fKey_User_Printed: number | null;
  fKey_User_LastPrint: number | null;
  dateTimeCompleted: string | null;
  reportSpeed: number;
  formatType: number | null;
  createdByFName: string;
  createdByLName: string;
  createByUserName: string;
  /**
   * Human-readable summary of the report's parameters, computed
   * server-side via the shared `ReportsSettingBase.ToString`
   * path used by the WinForms info popup. Empty string when the
   * report type is unknown to the shared factory or the settings
   * JSON is malformed. Rendered as-is in the info dialog.
   */
  infoText: string;
}

/** One row of POST api/report/Status: where a queued report document stands. */
export interface ReportDocumentStatus {
  pKey_RS_ReportDocument: number;
  status: number;
  guid: string | null;
  statusMessage: string | null;
}

/** Computed helpers for ReportDocument */
export function getDocumentDuration(doc: ReportDocument): number | null {
  if (doc.generatedOn && doc.dateTimeStarted) {
    return new Date(doc.generatedOn).getTime() - new Date(doc.dateTimeStarted).getTime();
  }
  return null;
}

export interface ReportFavorite {
  pKey_PM_User_ReportFavorite: number;
  fKey_Report: number;
  fKey_User: number;
  name: string;
}

export interface ReportDocumentParams {
  viewAll: boolean;
  testingMode: boolean;
  viewDeletedReports: boolean;
  viewTickets: boolean;
  pKeysManualProcess: number[];
  fKey_User: number;
}

export interface ReportResult {
  success: boolean;
  message: string;
  reportDocumentId: number | null;
  /** The same request was queued a moment ago (a double click): reportDocumentId is that report and nothing new was queued. */
  alreadyQueued?: boolean;
}

export interface QueueReportRequest {
  reportType: string;
  fKey_User: number;
  fKey_RS_Report: number;
  settingsJson: string;
  reportName: string;
  dateFor: string;
  /** Report category — matches WinForms `ReportCategory` enum:
   *  0 = Standard (default; shows in dashboard),
   *  1 = HiddenInDashboard (on-demand auto-show reports like
   *  Tray Ticket Visualizer). Omit / 0 for existing callers. */
  category?: number;
}

export interface ScheduledReport {
  pKey_RS_ScheduledReport: number;
  fKey_RS_Report: number;
  fKey_User: number;
  fKey_Facility: number;
  scheduleName: string;
  reportType: string;
  cronExpression: string;
  frequency: string;
  scheduledTime: string;
  daysOfWeek: string;
  dayOfMonth: number | null;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  settingsJson: string;
  dateTimeCreated: string;
  dateTimeModified: string | null;
  lastRunDateTime: string | null;
  nextRunDateTime: string | null;
  lastRunStatus: string;
  lastRunMessage: string;
  runCount: number;
  failureCount: number;
  createdBy: string;
  modifiedBy: string;
  isDeleted: boolean;
  notifyOnComplete: boolean;
  notifyOnFailure: boolean;
  notificationEmail: string;
  reportName?: string;
  /** How the saved settings follow the date rules (pages/reports/schedule/schedulePlan.ts). */
  schedulePlanJson?: string;
  /** The report form's values, to open its options again. */
  formValuesJson?: string;
  /** The next run, UTC. `nextRunDateTime` is the same moment on the facility's clock. */
  nextRunUtc?: string | null;
  /** The owner's name (shown to people who may see everyone's schedules). */
  ownerName?: string;
  fKey_Connection?: number | null;
}

export interface ScheduledReportLog {
  pKey_RS_ScheduledReportLog: number;
  fKey_RS_ScheduledReport: number;
  fKey_RS_ReportDocument: number | null;
  fKey_Facility: number;
  executionStartTime: string;
  executionEndTime: string | null;
  durationMs: number | null;
  status: string;
  statusMessage: string;
  errorDetails: string;
  triggeredBy: string;
  notificationSent: boolean;
  notificationSentAt: string | null;
  /** The occurrence a scheduler run was for (UTC); null for Run now. */
  scheduledForUtc?: string | null;
  /** The queued report's Print Queue status (REPORT_STATUS), while it is there. */
  documentStatus?: number | null;
}

export interface ScheduledReportResult {
  success: boolean;
  message: string | null;
  scheduledReport?: ScheduledReport | null;
}

export interface ReportGroup {
  reportGroupName: string;
  section: string;
  reports: Report[];
}

// ===== HELPER FUNCTIONS =====

export function getFrequencyDisplay(schedule: ScheduledReport): string {
  const time = parseTimeSpan(schedule.scheduledTime);
  const timeStr = formatTime12(time.hours, time.minutes);

  switch (schedule.frequency?.toLowerCase()) {
    case 'daily':
      return `Daily at ${timeStr}`;
    case 'weekly':
      return `${getWeekDaysDisplay(schedule.daysOfWeek)} at ${timeStr}`;
    case 'monthly':
      return `Day ${schedule.dayOfMonth} of each month at ${timeStr}`;
    default:
      return 'Not configured';
  }
}

export function getWeekDaysDisplay(daysOfWeek: string): string {
  if (!daysOfWeek) return 'No days';
  const days = daysOfWeek.split(',').map(Number);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  if (days.length === 7) return 'Every day';
  if (days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d))) return 'Weekdays';
  if (days.length === 2 && [0, 6].every((d) => days.includes(d))) return 'Weekends';
  return days.map((d) => dayNames[d]).join(', ');
}

export function parseTimeSpan(ts: string): { hours: number; minutes: number } {
  if (!ts) return { hours: 6, minutes: 0 };
  const parts = ts.split(':');
  return { hours: parseInt(parts[0], 10) || 0, minutes: parseInt(parts[1], 10) || 0 };
}

export function formatTime12(hours: number, minutes: number): string {
  const h = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  return `${h}:${String(minutes).padStart(2, '0')} ${ampm}`;
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

export function buildCronExpression(
  frequency: string,
  hours: number,
  minutes: number,
  daysOfWeek?: string,
  dayOfMonth?: number,
): string {
  switch (frequency) {
    case 'daily':
      return `${minutes} ${hours} * * *`;
    case 'weekly':
      return `${minutes} ${hours} * * ${daysOfWeek || '1,2,3,4,5'}`;
    case 'monthly':
      return `${minutes} ${hours} ${dayOfMonth || 1} * *`;
    default:
      return '0 6 * * *';
  }
}
